import { toast } from 'react-hot-toast';
import { NavigateFunction } from 'react-router-dom';
import styled from 'styled-components';

import i18n from '../../locale';
import { Notification } from '../../types/api.graphql';
import CustomToastContent from '../../views/toasts/CustomToastContent';
import { ImgProxyResizer } from '../ImageProxyResizer';

export enum NotificationActionType {
  bulkCreate = 'bulkCreate',
  transitionListing = 'transitionListing',
  createVehicle = 'createVehicle',
  ownerTradeCompleted = 'ownerTradeCompleted',
  vendorTradeCompleted = 'vendorTradeCompleted',
  vendorAssignedVehicle = 'vendorAssignedVehicle',
  mention = 'mention',
}

type MarkAsReadFunction = (id: string) => void;

interface ShowToastParams {
  markAsRead: MarkAsReadFunction;
  navigate: NavigateFunction;
}

interface ShowCustomToastParams extends Omit<ShowToastParams, 'navigate'> {
  navigate?: NavigateFunction;
  submitButtonTitle?: string;
  imageUrl?: string;
  squaredImage?: boolean;
  onSubmit?: () => void;
}

class NotificationHandler {
  protected notification: Notification;

  constructor(notification: Notification) {
    this.notification = notification;
  }

  public static createHandler(notification: Notification): NotificationHandler {
    if (
      notification.action === NotificationActionType.bulkCreate ||
      notification.action?.endsWith('Listing') ||
      notification.action?.endsWith('Vehicle')
    ) {
      return new ListingNotificationHandler(notification);
    }
    if (notification.action === NotificationActionType.mention) {
      return new MentionNotificationHandler(notification);
    }
    return new NotificationHandler(notification);
  }

  protected showCustomToast = ({
    markAsRead,
    submitButtonTitle,
    imageUrl,
    squaredImage,
    onSubmit,
  }: ShowCustomToastParams) => {
    const resizedImgUrl =
      imageUrl ||
      (this.notification.imageUrl && new ImgProxyResizer(this.notification.imageUrl).mediumAvatarSquaredUrl);

    toast.custom(
      <CustomToastContent
        messageHtml={this.notification.messageHtml}
        image={resizedImgUrl && <NotificationImage src={resizedImgUrl} $squared={squaredImage} />}
        dateCreatedAt={this.notification.createdAt}
        submitButtonTitle={submitButtonTitle}
        onSubmit={() => {
          if (!this.notification.clicked) {
            markAsRead(this.notification.id);
          }
          onSubmit?.();
        }}
        onDismiss={() => {
          if (!this.notification.clicked) {
            markAsRead(this.notification.id);
          }
        }}
      />,
      { duration: Infinity },
    );
  };

  public showToast({ markAsRead }: ShowToastParams) {
    this.showCustomToast({
      markAsRead,
      squaredImage: true,
    });
  }
}

interface VehicleNotificationImageProps {
  $squared?: boolean;
}

const NotificationImage = styled.img<VehicleNotificationImageProps>`
  width: ${(props: VehicleNotificationImageProps) => (props.$squared ? '2.5rem' : '3.25rem')};
  height: 2.5rem;
  object-fit: cover;
  border-radius: 0.375rem;
`;

class ListingNotificationHandler extends NotificationHandler {
  showToast({ markAsRead, navigate }: ShowToastParams) {
    this.showCustomToast({
      markAsRead,
      navigate,
      submitButtonTitle: i18n.t('notifications.viewVehicle'),
      imageUrl: this.notification.imageUrl && new ImgProxyResizer(this.notification.imageUrl).smallUrl,
      onSubmit: () => {
        if (this.notification.action === NotificationActionType.bulkCreate) {
          navigate('/app/listings/');
        } else {
          navigate(`/app/listings/${this.notification.entityId}`);
        }
      },
    });
  }
}

class MentionNotificationHandler extends NotificationHandler {
  showToast({ markAsRead, navigate }: ShowToastParams) {
    this.showCustomToast({
      markAsRead,
      navigate,
      submitButtonTitle: i18n.t('notifications.viewMention'),
      imageUrl: this.notification.imageUrl && new ImgProxyResizer(this.notification.imageUrl).mediumAvatarSquaredUrl,
      squaredImage: true,
      onSubmit: () => {
        navigate(`/app/listings/${this.notification.entityId}/activity#activity`);
      },
    });
  }
}

export default NotificationHandler;
